import { type TabsProps } from "antd";
import i18n from "../../i18n";
import { formatTranslation } from "../../shared/utils/translationUtils";
import CPBiological, {
  type CPBiologicalProps,
} from "./components/CPBiological";
import Nutrient from "./components/Nutrient";
import TabLabel from "./components/TabLabel";
import { type NutrientState } from "./hooks/useNutrientState";
import { type useNewTrialState } from "./legacy";

export const RATES_DOSAGE_STEP = 2;

export type RatesDosageTabKey = "seeds" | "biologicals" | "nutrient";

export interface GetTabsArgs
  extends Pick<
    CPBiologicalProps,
    | "newTrialState"
    | "trialInfo"
    | "onApplicationChange"
    | "onValidationChange"
    | "active"
    | "onRegisterBeforeLeave"
    | "onSavingChange"
    | "onRegisterReset"
    | "onResetAvailabilityChange"
    | "onNavigateToPlotDesign"
  > {
  trialProtocolData?: ReturnType<typeof useNewTrialState>["trialProtocolData"];
  nutrientState: NutrientState;
  completed: Record<RatesDosageTabKey, boolean>;
  biologicalsTabComplete: boolean;
  onBiologicalsCompletionChange?: (complete: boolean) => void;
  onNavigateToSeeds?: () => void;
  onContinueFromBiologicals?: () => void;
  onPlotSelectionActive?: (active: boolean) => void;
  onBiologicalsValidationChange?: (valid: boolean) => void;
}

export const getTabs = ({
  nutrientState,
  completed,
  biologicalsTabComplete,
  onBiologicalsCompletionChange,
  onNavigateToSeeds,
  onNavigateToPlotDesign,
  onContinueFromBiologicals,
  onPlotSelectionActive,
  onBiologicalsValidationChange,
  onApplicationChange,
  active,
  newTrialState,
  trialInfo,
  trialProtocolData,
  onRegisterBeforeLeave,
  onSavingChange,
  onRegisterReset,
  onResetAvailabilityChange,
}: GetTabsArgs): NonNullable<TabsProps["items"]> => {
  const productDetails = trialProtocolData?.product_details;
  const available: Record<RatesDosageTabKey, boolean> = {
    seeds: Boolean(productDetails?.seeds),
    biologicals: Boolean(productDetails?.biologicals),
    nutrient: Boolean(productDetails?.nutrients),
  };
  return [
    {
      key: "seeds",
      label: (
        <TabLabel
          label={formatTranslation(i18n.t("newRatesAndDosage.tabs.seeds"))}
          completed={completed.seeds}
        />
      ),
      children: null,
    },
    {
      key: "biologicals",
      forceRender: true,
      label: (
        <TabLabel
          label={formatTranslation(
            i18n.t("newRatesAndDosage.tabs.biologicals")
          )}
          completed={biologicalsTabComplete}
        />
      ),
      children: (
        <CPBiological
          newTrialState={newTrialState}
          trialInfo={trialInfo}
          trialProtocolData={trialProtocolData}
          hasSeeds={available.seeds}
          hasNutrients={available.nutrient}
          onContinue={onContinueFromBiologicals}
          onNavigateToSeeds={onNavigateToSeeds}
          onNavigateToPlotDesign={onNavigateToPlotDesign}
          onPlotSelectionActive={onPlotSelectionActive}
          onValidationChange={onBiologicalsValidationChange}
          onCompletionChange={onBiologicalsCompletionChange}
          onApplicationChange={onApplicationChange}
          active={active}
          onRegisterBeforeLeave={onRegisterBeforeLeave}
          onSavingChange={onSavingChange}
          onRegisterReset={onRegisterReset}
          onResetAvailabilityChange={onResetAvailabilityChange}
        />
      ),
    },
    {
      key: "nutrient",
      label: (
        <TabLabel
          label={formatTranslation(i18n.t("newRatesAndDosage.tabs.nutrient"))}
          completed={completed.nutrient}
        />
      ),
      children: <Nutrient state={nutrientState} />,
    },
  ].filter((item) => available[item.key as RatesDosageTabKey]);
};
